/**
 * The period of a reporting screen. It lives in the URL (TMM-B6, TMMRSL-0072): `period` is a
 * preset id, or `custom` with `from` / `to` (civil dates); the default, Current period, is omitted.
 * Arriving without a period, the screen takes the one kept in sessionStorage
 * (`tmm_reseller_admin.period[.tenant].<screen>`), then Current period, and doesn't write it into
 * the URL until the person picks one. Picking writes both, the URL replaced; the period in force is
 * always kept in sessionStorage, as before.
 */
import { useEffect, useRef, useState } from 'react';
import { FormControl, InputLabel, MenuItem, Select, Stack, TextField, useMediaQuery } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { type Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { stringParam, useUrlState } from '@/components/useUrlState';
import {
  isValidCustomRange,
  parseStored,
  periodStorageKey,
  rangeForStored,
  serialize,
  todayCivil,
  type PeriodPreset,
  type PeriodRange,
  type PeriodScope,
  type StoredPeriod,
} from '@/i18n/periods';
import { FIELD_WIDTH } from '@/theme';

const PRESETS: Exclude<PeriodPreset, 'custom'>[] = [
  'current',
  'last30',
  'last90',
  'thisMonth',
  'lastMonth',
];

const SCHEMA = { period: stringParam(), from: stringParam(), to: stringParam() };

export interface PeriodPickerProps {
  screen: string;
  /** Storage scope: reseller-wide screens, or a screen inside a tenant workspace (H §9.4). */
  scope?: PeriodScope;
  /** Params removed in the same write as a period change (a screen's open record). */
  clears?: readonly string[];
  onChange: (range: PeriodRange) => void;
}

/**
 * The period in the URL, or `null` when there is none or it isn't valid (a custom range must be a
 * valid one: from ≤ to ≤ today), so the screen falls back to sessionStorage, then the default.
 */
function fromUrl(values: { period: string; from: string; to: string }): StoredPeriod | null {
  if (values.period === 'custom') {
    return isValidCustomRange(values.from, values.to, todayCivil())
      ? { kind: 'custom', from: values.from, to: values.to }
      : null;
  }
  return (PRESETS as string[]).includes(values.period)
    ? { kind: 'preset', id: values.period as Exclude<PeriodPreset, 'custom'> }
    : null;
}

function readStored(screen: string, scope: PeriodScope): StoredPeriod | null {
  try {
    return parseStored(sessionStorage.getItem(periodStorageKey(screen, scope)));
  } catch {
    return null;
  }
}

function writeStored(screen: string, scope: PeriodScope, stored: StoredPeriod) {
  try {
    sessionStorage.setItem(periodStorageKey(screen, scope), serialize(stored));
  } catch {
    // sessionStorage may be unavailable; selection still works in-memory.
  }
}

function storedFromUi(
  preset: PeriodPreset,
  customFrom: string,
  customTo: string,
): StoredPeriod | null {
  if (preset !== 'custom') return { kind: 'preset', id: preset };
  if (!isValidCustomRange(customFrom, customTo, todayCivil())) return null;
  return { kind: 'custom', from: customFrom, to: customTo };
}

export function PeriodPicker({ screen, scope = 'reseller', clears, onChange }: PeriodPickerProps) {
  const { t } = useTranslation();
  const compact = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const today = todayCivil();
  const url = useUrlState(SCHEMA, clears === undefined ? {} : { clears });
  const inUrl = fromUrl(url.values);
  const urlKey = inUrl ? serialize(inUrl) : '';
  const [restored] = useState<StoredPeriod>(
    () => inUrl ?? readStored(screen, scope) ?? { kind: 'preset', id: 'current' },
  );
  const [preset, setPreset] = useState<PeriodPreset>(() =>
    restored.kind === 'custom' ? 'custom' : restored.id,
  );
  const [customFrom, setCustomFrom] = useState(() => (restored.kind === 'custom' ? restored.from : ''));
  const [customTo, setCustomTo] = useState(() => (restored.kind === 'custom' ? restored.to : ''));
  // The URL moved to another period (Back, a link): show it.
  const [seenUrl, setSeenUrl] = useState(urlKey);
  if (seenUrl !== urlKey) {
    setSeenUrl(urlKey);
    if (inUrl) {
      setPreset(inUrl.kind === 'custom' ? 'custom' : inUrl.id);
      if (inUrl.kind === 'custom') {
        setCustomFrom(inUrl.from);
        setCustomTo(inUrl.to);
      }
    }
  }
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const customInvalid =
    preset === 'custom' &&
    (customFrom.length > 0 || customTo.length > 0) &&
    !isValidCustomRange(customFrom, customTo, today);

  const effective = storedFromUi(preset, customFrom, customTo);
  const effectiveKey = effective ? serialize(effective) : null;
  // The period in force is kept in sessionStorage, as before the URL held it, and emitted.
  useEffect(() => {
    const next = effectiveKey === null ? null : parseStored(effectiveKey);
    if (next === null) return;
    writeStored(screen, scope, next);
    onChangeRef.current(rangeForStored(next, todayCivil()));
  }, [screen, scope, effectiveKey]);

  /**
   * The person picked a period: it goes into the URL, replaced (sessionStorage follows above).
   * Current period is the omitted default, so the query may not change; `forceClears` still
   * drops the screen's open record. Mount never calls this.
   */
  function persist(next: StoredPeriod | null) {
    if (next === null) return;
    url.set(
      next.kind === 'custom'
        ? { period: 'custom', from: next.from, to: next.to }
        : { period: next.id === 'current' ? '' : next.id, from: '', to: '' },
      { forceClears: true },
    );
  }

  function handlePreset(event: SelectChangeEvent<PeriodPreset>) {
    const value = event.target.value as PeriodPreset;
    setPreset(value);
    persist(storedFromUi(value, customFrom, customTo));
  }

  function handleFrom(value: string) {
    setCustomFrom(value);
    persist(storedFromUi(preset, value, customTo));
  }

  function handleTo(value: string) {
    setCustomTo(value);
    persist(storedFromUi(preset, customFrom, value));
  }

  return (
    <Stack
      direction={compact ? 'column' : 'row'}
      spacing={1}
      sx={{ flexWrap: 'wrap', alignItems: 'flex-start', width: compact ? 1 : 'auto' }}
    >
      <FormControl size="small" sx={{ minWidth: FIELD_WIDTH.sm }}>
        <InputLabel id={`period-${screen}-label`}>{t('period.label')}</InputLabel>
        <Select
          labelId={`period-${screen}-label`}
          label={t('period.label')}
          value={preset}
          onChange={handlePreset}
        >
          {PRESETS.map((id) => (
            <MenuItem key={id} value={id}>
              {t(`period.${id}`)}
            </MenuItem>
          ))}
          <MenuItem value="custom">{t('period.custom')}</MenuItem>
        </Select>
      </FormControl>
      {preset === 'custom' ? (
        <Stack direction="row" spacing={1} sx={{ width: compact ? 1 : 'auto' }}>
          <TextField
            type="date"
            size="small"
            label={t('period.from')}
            value={customFrom}
            onChange={(e) => handleFrom(e.target.value)}
            error={customInvalid}
            helperText={customInvalid ? t('period.invalidRange') : undefined}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { max: today },
            }}
          />
          <TextField
            type="date"
            size="small"
            label={t('period.to')}
            value={customTo}
            onChange={(e) => handleTo(e.target.value)}
            error={customInvalid}
            helperText={customInvalid ? t('period.invalidRange') : undefined}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { max: today },
            }}
          />
        </Stack>
      ) : null}
    </Stack>
  );
}
